import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { FALLBACK_BRANCHES } from "@/data/site";

export default function BranchDetail() {
  const { slug } = useParams();
  const [branches, setBranches] = useState(FALLBACK_BRANCHES);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/branches")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setBranches(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const b = branches.find((x) => x.slug === slug);
  const others = branches.filter((x) => x.slug !== slug);

  if (!loading && !b) {
    return (
      <main className="pt-40 pb-32 text-center">
        <h1 className="font-display uppercase text-4xl">Branch not found</h1>
        <Link data-testid="branch-notfound-back" to="/branches" className="mt-6 inline-block text-gold font-mono text-xs uppercase tracking-[0.2em]">
          ← All branches
        </Link>
      </main>
    );
  }

  if (!b) return <main className="min-h-screen" />;

  return (
    <main>
      <section className="relative min-h-[60vh] flex items-end overflow-hidden noise">
        <img src={b.image} alt={`Oxygen Gym ${b.name}`} className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/20" />
        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-8 lg:px-12 pb-16 pt-40">
          <Reveal>
            <Link data-testid="branch-detail-back" to="/branches" className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold hover:text-gold-light">
              ← All branches
            </Link>
            <h1 className="mt-6 font-display uppercase text-5xl sm:text-7xl leading-[0.95]">
              Oxygen Gym<br /><span className="text-gold-gradient">{b.name}</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-16 md:py-24">
        <div className="grid gap-10 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <div className="clip-frame border border-white/10 bg-panel p-8 md:p-10">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-6">Branch Details</p>
              <dl className="space-y-6">
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Address</dt>
                  <dd className="text-white/85">{b.address}</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Timings</dt>
                  <dd className="text-white/85">{b.timings}</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Phone / WhatsApp</dt>
                  <dd className="text-white/85">{b.phone}</dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Amenities</dt>
                  <dd className="flex flex-wrap gap-2 mt-2">
                    {b.amenities.map((a) => (
                      <span key={a} className="border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-white/65">
                        {a}
                      </span>
                    ))}
                  </dd>
                </div>
                <div>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Specialties</dt>
                  <dd className="flex flex-wrap gap-2 mt-2">
                    {b.specialties.map((s) => (
                      <span key={s} className="bg-gold/10 border border-gold/30 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] text-gold">
                        {s}
                      </span>
                    ))}
                  </dd>
                </div>
              </dl>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  data-testid={`branch-detail-enquiry-${b.slug}`}
                  to={`/contact?branch=${b.slug}`}
                  className="bg-gold text-ink px-8 py-4 font-bold uppercase text-xs tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300"
                >
                  Enquire For This Branch
                </Link>
                <a
                  data-testid={`branch-detail-whatsapp-${b.slug}`}
                  href={`https://wa.me/${b.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/25 text-white px-8 py-4 font-bold uppercase text-xs tracking-[0.15em] hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  WhatsApp
                </a>
                <a
                  data-testid={`branch-detail-map-${b.slug}`}
                  href={b.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/25 text-white px-8 py-4 font-bold uppercase text-xs tracking-[0.15em] hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  Open In Maps
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="clip-frame border border-white/10 bg-panel p-8 h-full">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-6">Other Houses</p>
              <div className="space-y-6">
                {others.map((o) => (
                  <Link key={o.slug} data-testid={`branch-detail-other-${o.slug}`} to={`/branches/${o.slug}`} className="group block">
                    <div className="aspect-[16/9] overflow-hidden clip-frame-sm border border-white/10">
                      <img
                        src={o.image}
                        alt={`Oxygen Gym ${o.name}`}
                        className="h-full w-full object-cover opacity-60 transition-all duration-500 group-hover:opacity-90 group-hover:scale-105"
                      />
                    </div>
                    <h3 className="mt-3 font-display uppercase text-lg group-hover:text-gold transition-colors">{o.name}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
